import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './config/theme/globalStyles.css';
import 'bootstrap-daterangepicker/daterangepicker.css';

import Header from './common/Header';
import Routes from './routes';
import AppConstants from './constants';

import { AppState } from './config/redux/reducers';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { resetUserError, fetchUserAccessControl } from './Login/redux/loginSlice';
import { clearDeletedRoute,clearBulkDeletedRoute, clearUpdatedRoute, resetCreateRouteSuccess, resetPlanningError, clearPublishedRoutesErrors, clearPublishedRoutes, resetDraftRoute } from './RoutePlanning/redux/planningSlice';
import { resetUsersError, resetUsersResetPwd, setLoggedInUser } from './Users/redux/usersSlice';
import { UACProvider } from './contexts/UACContext';

interface AppProps {
  location?: any;
  history: any;
}

const App = (props: AppProps) => {
  const { history } = props;
  const {
    isLoggedIn,
    error: userLoginError,
    role,
    uacData,
    uacLoading,
    uacError,
  } = useSelector((state: AppState) => state.userLogin);

  const { error: planningOrderListError, routeCreateSuccess, updatedRoute, deletedRoute, bulkDeletedRoute, draftRoute, publishedRoutes, publishedRoutesErrors } = useSelector((state: AppState) => state.routePlanning);
  const { error: usersError, resetPwdSuccess } = useSelector((state: AppState) => state.users);
  const { countryCode, userFeatureConfigDetails } = useSelector((state: AppState) => state.common);
 
  const dispatch = useDispatch(); 
  const hasHeader = isLoggedIn;

  const [openSideMenuOnClick, setOpenSideMenuOnClick] = useState(false);
  const uacPermissions = useMemo(() => uacData?.uac ?? [], [uacData]);

  const handleSideMenuOpen = (openState: boolean) => {
    setOpenSideMenuOnClick(openState);
  }

  useEffect(() => {
    const unlisten = history.listen((route: any) => {
      if (AppConstants.ROUTES.HOME === route.pathname) {
        dispatch(setLoggedInUser());
      }
      if (userLoginError) {
        dispatch(resetUserError());
      }
      if (planningOrderListError) {
        dispatch(resetPlanningError());
      }
      if (usersError) {
        dispatch(resetUsersError());
      }
      if (routeCreateSuccess) {
        dispatch(resetCreateRouteSuccess());
      }
      if (draftRoute.route) {
        dispatch(resetDraftRoute());
      }
      if (resetPwdSuccess) {
        dispatch(resetUsersResetPwd());
      }
      if (publishedRoutes.length) {
        dispatch(clearPublishedRoutes());
      }
      if (publishedRoutesErrors.length) {
        dispatch(clearPublishedRoutesErrors());
      }
      if (route.pathname !== AppConstants.ROUTES.PLANNING) {
        if (updatedRoute) {
          dispatch(clearUpdatedRoute());
        }
        if (deletedRoute) {
          dispatch(clearDeletedRoute());
        }
        if (bulkDeletedRoute) {
          dispatch(clearBulkDeletedRoute());
        }
      }
    });
    return () => {
      unlisten();
    }
  }, [history, planningOrderListError, userLoginError, usersError, updatedRoute, deletedRoute, bulkDeletedRoute, routeCreateSuccess, resetPwdSuccess, publishedRoutes, draftRoute, publishedRoutesErrors, dispatch]);

  // Load UAC permissions when user is logged in
  useEffect(() => {
    if (isLoggedIn && !uacData) {
      dispatch(fetchUserAccessControl());
    }
  }, [isLoggedIn, uacData, dispatch]);


  if (isLoggedIn && uacError && !uacData) {
    return (
      <div className="uac-error" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '16px' }}>
        <p>Failed to load access permissions. Please refresh the page.</p>
        <button onClick={() => dispatch(fetchUserAccessControl())} style={{ padding: '8px 16px', cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }
  if (isLoggedIn && (uacLoading || !uacData)) {
    return <div className="uac-loading" />;
  }

  return (
    <UACProvider uacPermissions={uacPermissions}>
      {hasHeader && <Header openSideMenuOnClick={openSideMenuOnClick} handleDrawerOnClick={handleSideMenuOpen} />}
      <main className={`routesContainer ${hasHeader ? "hasHeader" : ""} ${openSideMenuOnClick ? "open" : ""}`}>
        <Routes
          role={role}
          isLoggedIn={isLoggedIn}
          countryCode={countryCode}
          features={userFeatureConfigDetails}
          uacPermissions={uacPermissions}
          defaultMenu={uacData?.defaultMenu || undefined}
        />
      </main>
    </UACProvider>
  );
};

export default withRouter(App);

